import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';
import { certificationLevels } from '@/mocks/certificateData';

const levelImages: Record<string, string> = {
  associate: '/images/generated/cert-associate-2026.jpg',
  professional: '/images/generated/cert-professional-2026.jpg',
  master: '/images/generated/cert-master-2026.jpg',
};

export default function CertificationCards() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();
  return (
    <section className="relative py-20 md:py-28 bg-background-100/60">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-14">
          {String(t('certificate.levels.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('certificate.levels.title')}
          </h2>
          {String(t('certificate.levels.titleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-500 text-[15px] tracking-[0.12em] mb-4 font-label">{__s}</p>))}
          <div className="w-10 h-[1px] bg-accent-500/40 mx-auto mb-6"></div>
          {String(t('certificate.levels.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] md:text-[16px] text-foreground-500 tracking-[0.04em] leading-relaxed max-w-[700px] mx-auto">{__s}</p>))}
        </div>

        {String(t('certificate.levels.certNote')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-center text-[14px] text-foreground-400 tracking-[0.04em] mb-10">{__s}</p>))}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {certificationLevels.map((level, idx) => (
            <div
              key={level.id}
              className="card-glow group bg-background-50 border border-background-200/60 overflow-hidden flex flex-col"
            >
              <div className="relative aspect-[3/2] overflow-hidden">
                <img
                  src={levelImages[level.id]}
                  alt={lang === 'en' ? t(`certificate.level.${idx}.name`) : level.name}
                  className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-4 left-4">
                  <span className="text-[13px] tracking-[0.12em] text-foreground-50 bg-accent-500/90 px-2.5 py-1">
                    {level.level}
                  </span>
                </div>
              </div>

              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-heading text-[17px] text-foreground-900 tracking-[0.05em] mb-1 group-hover:text-accent-600 transition-colors">
                  {lang === 'en' ? t(`certificate.level.${idx}.name`) : level.name}
                </h3>
                <p className="text-accent-500 text-[13px] tracking-[0.1em] mb-3 font-label">
                  {level.nameEn}
                </p>

                <p className="text-[14px] text-foreground-500 tracking-[0.04em] leading-relaxed mb-4">
                  {lang === 'en' ? t(`certificate.level.${idx}.lead`) : level.lead}
                </p>

                <div className="mt-auto pt-3 border-t border-background-200/60">
                  <Link
                    to={level.courseHref}
                    className="text-[13px] text-accent-500 hover:text-accent-400 tracking-[0.06em] transition-colors"
                  >
                    {t('certificate.levels.courseLabel')}{lang === 'en' ? t(`certificate.level.${idx}.course`) : level.course}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
